
'use client';

import { Header } from "@/components/landing/header";
import { Footer } from "@/components/landing/footer";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { projectsData } from "@/lib/projects-data";
import { Plus, Laptop, School, BarChart, Bot, Briefcase, Zap } from "lucide-react";

// Section 1: Hero
function ProjectsHero() {
  return (
    <section className="relative w-full min-h-[70vh] flex items-center justify-center pt-28 pb-12 overflow-hidden liquid-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
            <div className="animate-fade-in-up flex flex-col items-center justify-center space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel-intense border border-white/20 text-white/90 text-sm font-medium mb-4">
                    <span className="w-2 h-2 rounded-full bg-neon-purple animate-pulse"></span>
                    Showcasing Excellence
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold leading-[1.1] tracking-tight drop-shadow-2xl text-white">
                    Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-purple to-neon-blue drop-shadow-sm">Success Stories</span>
                </h1>
                <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed font-light tracking-wide">
                    Explore our diverse portfolio of innovative solutions that have transformed businesses across industries.
                </p>
            </div>
        </div>
    </section>
  );
}

// Section 2: Portfolio
const categories = ['All', 'Desktop App', 'EdTech', 'Data Analytics', 'SaaS', 'Web Portal', 'Corporate Website'];

const projectIcons: { [key: string]: React.ReactNode } = {
  'Laptop': <Laptop />,
  'School': <School />,
  'BarChart': <BarChart />,
  'Bot': <Bot />,
  'Briefcase': <Briefcase />,
  'Zap': <Zap />,
};

function Portfolio() {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredProjects = selectedCategory === 'All'
    ? projectsData
    : projectsData.filter(p => p.category === selectedCategory);

  const getButtonClass = (category: string, isActive: boolean) => {
    if (!isActive) {
      return "glass-panel-intense text-slate-600 dark:text-slate-300 border-transparent hover:border-primary hover:text-primary";
    }
    switch (category) {
        case 'Desktop App':
            return 'bg-blue-500 text-white border-blue-500';
        case 'EdTech':
            return 'bg-green-500 text-white border-green-500';
        case 'Data Analytics':
            return 'bg-purple-500 text-white border-purple-500';
        case 'SaaS':
            return 'bg-cyan-500 text-white border-cyan-500';
        case 'Web Portal':
            return 'bg-orange-500 text-white border-orange-500';
        case 'Corporate Website':
            return 'bg-indigo-500 text-white border-indigo-500';
        default: // for "All"
            return 'bg-primary text-primary-foreground border-primary';
    }
  };

  return (
    <section className="py-24 bg-background relative overflow-hidden" id="portfolio">
      <div className="absolute top-0 left-0 w-full h-full bg-grid-slate-200/50 dark:bg-grid-slate-800/50 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10"></div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800 dark:text-white mb-4">Our Digital Showcase</h2>
            <p className="text-slate-600 dark:text-slate-400">Filter through our case studies to see how we've helped businesses like yours succeed.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up mb-16">
          {categories.map(category => {
            const isActive = selectedCategory === category;
            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={cn(
                  "px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 hover:scale-105 active:scale-95 border",
                  getButtonClass(category, isActive)
                )}
              >
                {category}
              </button>
            )
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map(project => (
            <Link key={project.slug} href={`/projects/${project.slug}`} className={`group h-full rounded-3xl glass-panel-intense border border-slate-200 dark:border-slate-700 neon-border-${project.color} relative overflow-hidden cursor-pointer flex flex-col transition-all duration-500 hover:-translate-y-4 hover:rotate-1`}>
              <div className="relative h-64 overflow-hidden">
                <div className={`absolute inset-0 bg-${project.color}-900/20 group-hover:bg-transparent transition-colors z-10`}></div>
                <Image alt={`${project.title} Preview`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" height={256} width={400} src={project.image} />
                <div className="absolute top-4 right-4 z-20">
                  <span className={`px-3 py-1 text-xs font-bold rounded-full bg-${project.color}-500/90 text-white backdrop-blur-sm shadow-lg`}>{project.category}</span>
                </div>
              </div>
              <div className="p-8 flex flex-col flex-grow relative">
                <div className={`absolute inset-0 bg-gradient-to-br from-${project.color}-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}></div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-2xl font-bold text-slate-800 dark:text-white group-hover:text-${project.color}-500 transition-colors`}>{project.title}</h3>
                  <div className={`text-slate-400 group-hover:text-${project.color}-500 group-hover:animate-pulse-fast`}>{projectIcons[project.icon]}</div>
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-6 flex-grow">
                  {project.description}
                </p>
                <div className={`w-full mt-auto py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-transparent text-slate-700 dark:text-slate-300 text-sm font-bold group-hover:bg-dark-navy group-hover:text-white transition-all flex items-center justify-center`}>
                  View Case Study
                </div>
              </div>
            </Link>
          ))}
          <div className="group h-full rounded-3xl glass-panel-intense border border-dashed border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500 relative overflow-hidden cursor-pointer flex flex-col items-center justify-center transition-all duration-300 p-8 min-h-[400px]">
            <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
              <Plus className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-600 dark:text-slate-300 mb-2">Many Others</h3>
            <p className="text-slate-500 dark:text-slate-500 text-sm text-center">
              More exciting projects are in the pipeline.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Section 3: CTA
function CTA() {
    return (
        <section className="py-24 relative overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 bg-dark-navy z-0">
                <div className="absolute inset-0 liquid-bg opacity-30"></div>
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20"></div>
            </div>
            <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
                <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-8 tracking-tight drop-shadow-lg">
                    Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-green to-neon-blue">Build Your Own?</span>
                </h2>
                <p className="text-xl text-slate-200 mb-10 max-w-2xl mx-auto">
                    Bring your vision to life with our expert development team. Let's create the next big thing together.
                </p>
                <Link href="/contact?inquiry=quotation&source=projects">
                    <button className="px-10 py-5 bg-white text-primary rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 flex items-center justify-center mx-auto group">
                        Get Started
                    </button>
                </Link>
            </div>
        </section>
    )
}

// Main Page Component
export default function ProjectsPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main>
        <ProjectsHero />
        <Portfolio />
        <CTA />
      </main>
      <Footer />
    </div>
  );
}
